import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { keyBy } from 'lodash-es';
import { DragulaService } from 'ng2-dragula';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';

import {
  Field,
  FolderKinds,
  RegisterViewColumnsSetting,
  List,
  Template,
} from '@site-mate/dashpivot-shared-library';

import { TemplatesService } from 'app/apps/templates.service';
import { ListsService } from 'app/lists/lists.service';
import { TeamWeb } from 'app/shared/model/team.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { TeamService } from 'app/shared/service/team.service';
import { ToastrService } from 'app/shared/service/toastr.service';

@Component({
  selector: 'cc-register-view-setting',
  templateUrl: 'register-view-setting.component.html',
  styleUrls: ['register-view-setting.component.scss'],
})
export class RegisterViewSettingComponent implements OnInit, OnDestroy {
  @Input() template: Template;

  @ViewChild('fieldSettingsContainer')
  fieldSettingsContainer: ElementRef;

  private unSubscribe = new Subject<void>();
  readonly dragulaBag = 'reg-view-column-setting-bag';

  setting: RegisterViewColumnsSetting;
  loading: boolean;
  templateFields: { [key: string]: Field };
  metadataList$ = new BehaviorSubject<List[]>(null);
  isSelectAllChecked: boolean;
  isSaveButtonDisabled: boolean;

  constructor(
    public readonly modal: NgbActiveModal,
    private readonly templatesService: TemplatesService,
    private readonly toastr: ToastrService,
    private readonly errorHandler: ErrorHandler,
    private readonly dragulaService: DragulaService,
    private readonly teamService: TeamService,
    private readonly listsService: ListsService,
  ) {}

  ngOnInit(): void {
    this.dragulaService.createGroup(this.dragulaBag, {
      moves: (el, container, handle) => {
        return !handle.closest('.dependent');
      },
    });

    this.setupMetadataList();

    this.loading = true;

    this.templateFields = keyBy(this.template.items, '_id');
    this.templatesService
      .getRegisterViewColumnsSetting(this.template.id)
      .pipe(takeUntil(this.unSubscribe))
      .subscribe(
        (setting) => {
          this.setting = setting;
          this.setSelectAll();
          this.isSaveButtonDisabled = this.getSaveButtonDisabledState();

          this.loading = false;
        },
        (error) => {
          this.loading = false;
          this.errorHandler.handle(error);
        },
      );
  }

  ngOnDestroy(): void {
    this.dragulaService.destroy(this.dragulaBag);

    this.unSubscribe.next();
    this.unSubscribe.complete();
  }

  setupMetadataList() {
    this.teamService.currentTeam
      .pipe(
        takeUntil(this.unSubscribe),
        filter((team: TeamWeb) => !!team.id),
        switchMap((team) => {
          if (this.teamService.isTeam(team)) {
            return this.listsService.getMetaDataList(FolderKinds.Project, team.projectId);
          }
          if (this.teamService.isCompany(team)) {
            return this.listsService.getMetaDataList(FolderKinds.Company, team.id);
          }

          return [];
        }),
      )
      .subscribe(
        (lists) => {
          this.metadataList$.next(lists);
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
  }

  save() {
    const fieldSettingElements: any[] = Array.from(
      this.fieldSettingsContainer.nativeElement.querySelectorAll('cc-register-view-setting-field'),
    );

    const mappedFieldSettings = keyBy(this.setting.fields, 'itemId');
    const fields = fieldSettingElements.map((element) => mappedFieldSettings[element.dataset.fieldId]);

    this.templatesService
      .updateRegisterViewColumnsSetting(this.template.id, { ...this.setting, fields })
      .subscribe(
        () => {
          this.toastr.successByKey('regViewSettingSaved');
          this.templatesService.onRegisterViewSettingChange();
        },
        (error) => {
          this.errorHandler.handle(error);
        },
      );
    this.modal.close();
  }

  handleSelectAll() {
    this.setting.fields = this.setting.fields.map((field) => {
      field.visible = this.isSelectAllChecked;
      return field;
    });
    this.isSaveButtonDisabled = this.getSaveButtonDisabledState();
  }

  setSelectAll() {
    this.isSelectAllChecked = this.setting.fields.every((field) => field.visible);
    this.isSaveButtonDisabled = this.getSaveButtonDisabledState();
  }

  get isWorkflow() {
    return this.template.columns.length > 0;
  }

  get hasFormCounter() {
    return this.template.counterIsActivated;
  }

  updateSaveButtonState() {
    this.isSaveButtonDisabled = this.getSaveButtonDisabledState();
  }

  getSaveButtonDisabledState() {
    if (!this.setting || !this.setting.fields || !this.setting.metadata) {
      return true;
    }
    const { fields, metadata } = this.setting;
    const {
      showProject,
      showTeam,
      showCreatedBy,
      showFormCounter,
      showWorkFlowStep,
      showCreatedOn,
      showDocVersion,
      showFormId,
      showLastUpdatedOn,
    } = metadata;

    const areAllMetadataFieldsHidden =
      !showProject &&
      !showTeam &&
      !showCreatedBy &&
      !showFormCounter &&
      !showWorkFlowStep &&
      !showCreatedOn &&
      !showDocVersion &&
      !showFormId &&
      !showLastUpdatedOn;

    const areAllFieldsHidden = fields.every((field) => !field.visible);

    return areAllMetadataFieldsHidden && areAllFieldsHidden;
  }
}
